import { EmbedLegacyProps } from "@ihr-radioedit/inferno-core";
import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { ILog } from "@ihr-radioedit/inferno-core";
import { isWindowDefined } from "@inferno/renderer-shared-core";

declare global {
  interface Window {
    FB: { XFBML: { parse?: () => void }; init: (opts: any) => void };
    twttr: { widgets: { load: () => void } };
    instgrm: { Embeds: { process: () => void } };
  }
}

const scriptLog = ILog.logger("activateScripts/embed.ts");
const executedScripts = new Map<string, Promise<unknown>>();
const done = Promise.resolve();
const htmlBlockContent = "[data-html-block-content]";
const javascriptType = /^(module|(text|application)\/(x-)?(java|ecma)script)?$/i;

const scriptKey = (script: HTMLScriptElement) =>
  script.hasAttribute("src") ? `src:${script.src}` : `inline:${script.type}:${script.text}`;

const willExecute = (script: HTMLScriptElement) =>
  !script.hasAttribute("nomodule") && javascriptType.test(script.type.trim());

const isSyncExternal = (script: HTMLScriptElement) =>
  script.hasAttribute("src") &&
  !script.hasAttribute("async") &&
  !script.hasAttribute("defer") &&
  script.type.trim().toLowerCase() !== "module";

const callsDocumentWrite = (script: HTMLScriptElement) =>
  /document\s*(\?\.|\.)\s*write|document\s*(\?\.)?\s*\[\s*["'`]write/.test(script.text);

const markDone = (script: HTMLScriptElement) => {
  const key = scriptKey(script);
  if (!executedScripts.has(key)) {
    executedScripts.set(key, done);
  }
};

export function markParsedScripts(root: ParentNode = document) {
  root
    .querySelectorAll<HTMLScriptElement>(`script[src], ${htmlBlockContent} script`)
    .forEach(script => willExecute(script) && markDone(script));
}

function cloneScript(inert: HTMLScriptElement) {
  const script = document.createElement("script");
  Array.from(inert.attributes).forEach(({ name, value }) => script.setAttribute(name, value));
  script.text = inert.text;
  return script;
}

const whenSettled = (script: HTMLScriptElement) =>
  new Promise(resolve => {
    script.addEventListener("load", resolve);
    script.addEventListener("error", resolve);
  });

const untilAborted = (settled: Promise<unknown>, signal?: AbortSignal) =>
  signal
    ? Promise.race([settled, new Promise(resolve => signal.addEventListener("abort", resolve, { once: true }))])
    : settled;

let activationQueue: Promise<void> = Promise.resolve();

export function activateScripts(container: HTMLElement, label?: string, signal?: AbortSignal) {
  activationQueue = activationQueue.catch(() => undefined).then(() => runScripts(container, label, signal));
  return activationQueue;
}

async function runScripts(container: HTMLElement, label?: string, signal?: AbortSignal) {
  for (const inert of Array.from(container.querySelectorAll("script"))) {
    if (signal?.aborted) {
      return;
    }
    if (!inert.isConnected || !willExecute(inert)) {
      continue;
    }
    const key = scriptKey(inert);
    const executed = executedScripts.get(key);
    if (executed) {
      if (isSyncExternal(inert)) {
        await untilAborted(executed, signal);
      }
      continue;
    }
    if (!inert.hasAttribute("src") && callsDocumentWrite(inert)) {
      scriptLog.warn(`Skipped an inline script that calls document.write in HTML block ${label ?? "(unknown)"}`);
      continue;
    }
    const script = cloneScript(inert);
    const settled = inert.hasAttribute("src") ? whenSettled(script) : done;
    executedScripts.set(key, settled);
    inert.replaceWith(script);
    if (isSyncExternal(inert)) {
      await untilAborted(settled, signal);
    }
  }
}

// hacky way to embed scripts via div placeholder; used for embedding scripts within react/jsx
export function activateEmbeds(div: HTMLDivElement) {
  div.querySelectorAll(".component-embed-script").forEach(placeholder => {
    if (!placeholder.classList.contains("activated") && placeholder instanceof HTMLDivElement) {
      const script = document.createElement("script");
      script.setAttribute("src", placeholder.dataset.src || "");
      script.setAttribute("async", "true");
      script.setAttribute("defer", "true");
      const key = scriptKey(script);
      if (!executedScripts.has(key)) {
        executedScripts.set(key, whenSettled(script));
      }
      placeholder.appendChild(script);
      placeholder.classList.add("activated");
    }
  });
}

export const attributeDimensionToInt = (attr: string | number | null | undefined) => {
  if (typeof attr === "number") {
    return attr;
  } else if (typeof attr === "string" && !attr.includes("%")) {
    // if string is only number or is px value, use parseInt (IHRAL-6431)
    if (/^\d+$/i.test(attr) || attr.includes("px")) {
      return parseInt(attr, 10);
    }
  }
  return 0;
};

export const hasValidDimension = (attr: string | number | null | undefined): boolean => {
  if (attr === null || attr === undefined || attr === "null" || attr === "undefined") {
    return false;
  }
  if (typeof attr === "string" && attr.includes("%")) {
    return parseFloat(attr) > 0;
  }
  return attributeDimensionToInt(attr) > 0;
};

export const dimensionToStyle = (attr: string | number | null | undefined): string => {
  if (!hasValidDimension(attr)) {
    return "auto";
  }
  return String(attr).includes("%") ? String(attr) : `${attributeDimensionToInt(attr)}px`;
};

export const getResponsiveStyles = (width: string, height: string) => {
  const styles: CSSProperties = {};
  const reg = RegExp("%", "i");
  const modifier = 1.1;

  if (width && height) {
    const intWidth = attributeDimensionToInt(width);
    const intHeight = attributeDimensionToInt(height);
    const percentageWidth = reg.test(width);
    const percentageHeight = reg.test(height);
    styles.height = 0;
    if (percentageWidth && !percentageHeight) {
      styles.paddingBottom = `${intHeight * modifier}px`;
    } else if (!percentageWidth && percentageHeight) {
      styles.paddingBottom = `${intHeight * modifier}%`;
    } else {
      const aspect_ratio = (intHeight / intWidth) * 100 * modifier;
      styles.paddingBottom = `${aspect_ratio}%`;
    }
  }
  return styles;
};

export interface GenericEmbedDetails {
  embed_type: string;
  provider: string;
  url: string | undefined;
}

export const getGenericEmbed = (props: EmbedLegacyProps) => {
  const details: GenericEmbedDetails = {
    embed_type: props.embed_type,
    provider: props.provider || "",
    url: props.url,
  };
  if (!props.url) {
    return details;
  }
  try {
    const url = new URL(props.url);
    if (url.hostname.includes("facebook.com")) {
      details.provider = "facebook";
      if (url.pathname.includes("video.php")) {
        details.embed_type = "video";
        const href = url.searchParams.get("href");
        if (href) {
          details.url = href;
        }
      }
    } else if (url.hostname.includes("youtube.com")) {
      details.provider = "youtube";
      details.embed_type = "video";
    }

    return details;
  } catch (err) {
    return details;
  }
};

export const vendorRefreshFns: any = {
  Facebook: () => window.FB && window.FB.XFBML.parse && window.FB.XFBML.parse(),
  Twitter: () => window.twttr && window.twttr.widgets.load(),
  Instagram: () => window.instgrm && window.instgrm.Embeds.process(),
};

export const useElementOnScreen = <T extends HTMLElement>(options: IntersectionObserverInit) => {
  const log = ILog.logger("useElementOnScreen/embed.ts");
  const [isViewable, setIsViewable] = useState<boolean>(false);
  const containerRef = useRef<T>(null);
  const entryRef = useRef<IntersectionObserverEntry | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      log.debug(
        `id: ${entry.target.id ?? "no-id"} // class: ${entry.target.className} // intersecting: ${
          entry.isIntersecting
        }`,
      );

      if (entry.intersectionRatio > 0) {
        setIsViewable(entry.isIntersecting);
        entryRef.current = entry;
        observer.current?.disconnect();
      }
    },
    [log, observer, entryRef],
  );

  useEffect(() => {
    if (isWindowDefined()) {
      observer.current = new IntersectionObserver(e => handleIntersect(e), options);
      if (containerRef.current) {
        observer.current.observe(containerRef.current);
      }
      return () => observer.current?.disconnect();
    }
  }, [containerRef, options, handleIntersect, observer]);

  return [containerRef, isViewable, entryRef] as const;
};
